import { ConfirmationType, LocationCardMonitorSeries, TritonConfiguration } from 'app/shared/models/monitorsetting';
import { InstallationType, LocationDetails } from 'app/shared/models/location-details';
import { NormalizedFormState } from '../config.models';
import { Customer } from 'app/shared/models/customer';
import { Locations, RainLocation } from 'app/shared/models/locations';
import { EntityResponseItem } from 'app/shared/models/view-data';
import { LocationCardData, RainGaugeLocation, SensorPreference } from '../../models/location-card.model';
import { Selectable } from 'app/shared/models/selectable';
import { BASE_MONITOR_SERIES } from '../constants/base';
import { EchoMonitorConfiguration } from '../configs/echo/models';
import { TritonMonitorConfiguration } from '../configs/triton/models';
import { ForesiteULMonitorConfiguration } from '../configs/foresite-ul/models';
import { ForesiteXDMonitorConfiguration } from '../configs/foresite-xd/models';
import { RainAlertMonitorConfiguration } from '../configs/rain-alert/models';
import { FlexMonitorConfiguration } from '../configs/flex/models';
import { EdgeRlMonitorConfiguration } from '../configs/edge-rl/models';
import { AvailableDynamicSection, DynamicSection } from '../models/dynamic-section-models';
import { Config } from 'app/shared/services/config';
import { BaseMonitorSeriesValues } from '../../location-card.constants';
import { DeviceTypeCode } from 'app/shared/enums/device-type-code';


export type MonitorConfiguration =
    | EchoMonitorConfiguration
    | TritonMonitorConfiguration
    | TritonConfiguration
    | ForesiteULMonitorConfiguration
    | ForesiteXDMonitorConfiguration
    | RainAlertMonitorConfiguration
    | FlexMonitorConfiguration
    | EdgeRlMonitorConfiguration;


export interface ConfirmationInput {
    title: string;
    message: string;
    okText: string;
    cancelText: string;
}

export const locationCardFeatureKey = 'locationCard';

export interface OptionsState<T> {
    data: T[];
    loading?: boolean;
}

export interface LocationCardSettings {
    isMetric: boolean;
    elevationUnit: string;
    depthUnit: string;
    tempUnit: string;
    flowUnit: string;
    voltUnit: string;
    dateFormat: string;
}

export interface LocationCardPermissions {
    isMonitorEditor: boolean;
    isBasicDataEditingAllowed: boolean;
    isAdmin: boolean;
}

export interface DiagnosticsState {
    showDiagnostics: boolean;
    deviceTypes: DeviceTypeCode[];
}

export enum FlexCreateMode {
    init = 'initial',
    error = 'error',
    wait = 'wait',
    success = 'success'
}

export interface LocationCardState<T> {
    locationDetails: LocationDetails[];
    locationCardData: LocationCardData;
    monitorConfiguration: T;
    formState: NormalizedFormState;
    customer: Customer;
    locations: Locations[];
    allMonitorSeries: LocationCardMonitorSeries[];
    options: {
        installationTypes?: OptionsState<InstallationType>;
        modemProviders?: OptionsState<Selectable>;
        rainGauges?: OptionsState<RainLocation>;
        qFinalEntities?: OptionsState<EntityResponseItem>;
        monitorSeries: OptionsState<LocationCardMonitorSeries>;

        // triton options
        depthPreferencesFirst: SensorPreference[];
        velPreferencesFirst: SensorPreference[];
        depthPreferencesSecond: SensorPreference[];
        velPreferencesSecond: SensorPreference[];

        // dynamic sections
        dynamicSections?: AvailableDynamicSection[];
        loadedDynamicConfigs?: DynamicSection[];
    };
    autocompleteOptions: {
        rainGauges?: RainLocation[];
        tagKeys: string[];
    };
    settings?: LocationCardSettings;
    permissions: LocationCardPermissions;
    diagnostics: DiagnosticsState;
    loading: boolean;
    fromHomeDashboard: boolean;

    confirmation: {
        show: boolean;
        data: ConfirmationInput;
        action: ConfirmationType;
    };

    pristine: boolean;
    valid: boolean;

    metadata: {
        flexCreateMode?: FlexCreateMode;
        previousInstallationType?: string;
        monitorSeriesName?: string;
    }
}

// Initialize with normalized state
export const initialState: LocationCardState<unknown> = {
    locationDetails: [],
    locationCardData: null,
    monitorConfiguration: null,
    customer: null,
    locations: null,
    formState: null,
    allMonitorSeries: [],
    options: {
        monitorSeries: {
            // #46083 Temporarily disabling FLEX monitor on Test and Prod environments
            data: BASE_MONITOR_SERIES.filter(v => (Config.isTestEnv || Config.isProdEnv) ? (v.value !== BaseMonitorSeriesValues.FLEX) : true),
        },
        installationTypes: {
            data: [],
        },
        modemProviders: {
            data: [],
        },
        rainGauges: {
            data: [],
        },
        qFinalEntities: {
            data: [],
        },

        // triton options
        depthPreferencesFirst: [],
        velPreferencesFirst: [],
        depthPreferencesSecond: [],
        velPreferencesSecond: [],
        dynamicSections: [],
        loadedDynamicConfigs: [],
    },
    autocompleteOptions: {
        rainGauges: [],
        tagKeys: [],
    },
    settings: null,
    permissions: {
        isMonitorEditor: false,
        isBasicDataEditingAllowed: false,
        isAdmin: false,
    },
    diagnostics: {
        showDiagnostics: false,
        deviceTypes: [],
    },
    loading: false,

    pristine: true,
    valid: false,
    fromHomeDashboard: false,

    confirmation: {
        show: false,
        data: {
            title: 'ADD_EDIT.EDIT_CONFIRM_DIALOG',
            message: 'ADD_EDIT.EDIT_CONFIRM_MSG',
            cancelText: 'COMMON.TEST_NO',
            okText: 'COMMON.TEST_YES',
        },
        action: null,
    },

    metadata: {}
};
